import { css, HelpIcon, styled } from 'canoe';
import { connectWithHammock, ENDPOINTS } from 'hammock';
import debounce from 'lodash/debounce';
import moment from 'moment';
import React, { useMemo, useState } from 'react';

import { ContentMetadata, ContentType } from '@candu-ai/content/types';
import { TextButton, ValueBox, ValueSelectInput } from '@candu-ai/craft/ui';
import { useWorkspacePath } from 'commons/hooks';
import { CONTENT_PUBLISHED_STATES } from 'commons/models';
import { ToolboxErrorMessage } from 'craft/components/ToolboxErrorMessage';
import { BuiltInComponents, CRAFT_ROOT_ID } from 'craft/constants';
import { useEditor } from 'craft/hooks';
import { ChangeContentAction } from 'craft/types';
import { contentPath } from 'dashboard/pages/Content';
import ContentPublishedState from 'dashboard/pages/Content/components/ContentPublishedState';

import { InteractionField, InteractionFieldProps } from './interactionFields';

interface StateProps {
  semaphoreOnly: boolean;
  content: any;
}

const State = styled(ContentPublishedState)<StateProps>`
  height: 8px;
  margin-left: 6px;
  margin-top: 5px;
  width: 8px;
`;

const OptionContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    background-color: ${theme.colors.BerryBlueL30};
    color: ${theme.colors.BerryBlue};
    padding: 7px 9px;

    svg {
      margin-right: 8px;
    }
  `}
`;

interface ContentListProp {
  contentList: ContentMetadata[];
}

type Item = {
  id: string;
  value: string;
  label: string;
  prefix?: React.ReactNode;
  disabled?: boolean;
  intent?: string;
};

const ChangeContentFieldImpl = ({
  defaultValue,
  onChange,
  contentList,
}: InteractionFieldProps<ChangeContentAction['handler']> & ContentListProp) => {
  const workspacePath = useWorkspacePath();
  const { query } = useEditor();
  const rootTypeName = query.node(CRAFT_ROOT_ID)?.get()?.data?.name;
  const [searchValue, setSearchValue] = useState('');

  const options = contentList
    .filter(
      ({ contentType }) =>
        (rootTypeName === BuiltInComponents.Document && contentType === ContentType.Inline) ||
        (rootTypeName === BuiltInComponents.Modal && contentType === ContentType.Modal) ||
        (rootTypeName === BuiltInComponents.Hotspot && contentType === ContentType.HotspotGroup),
    )
    .sort((content) => moment(content.lastModifiedAt).millisecond())
    .map((content) => ({
      id: content.id,
      value: content.slug,
      label: content.name,
      prefix: (
        <State
          semaphoreOnly
          content={{
            lastModifiedAt: content.lastModifiedAt,
            lastPublishedAt: content.lastPublishedAt,
            publishedState: content.lastPublishedAt
              ? CONTENT_PUBLISHED_STATES.PUBLISHED
              : CONTENT_PUBLISHED_STATES.DRAFT,
          }}
        />
      ),
    }));

  const filteredOptions = useMemo(() => {
    if (!searchValue) {
      return options;
    }
    return options.filter(({ label }: { label: string }) => {
      return label.toLowerCase().includes(searchValue?.toLowerCase());
    });
  }, [searchValue, options]);

  const publishedContentValue = !!contentList?.find(
    (value) => value.slug === defaultValue?.slug && value.lastPublishedAt,
  );

  const updateSearchValue = debounce((e) => {
    setSearchValue(e.target.value);
  }, 300);

  const clearSearchValue = () => setSearchValue('');

  const onSelectValue = (select: { value: string } | null) => {
    onChange({ ...defaultValue, slug: select?.value });
  };

  const selectedItem = filteredOptions.find(({ value }) => value === defaultValue?.slug);

  return (
    <>
      <ValueBox isInGroup={{ isLast: true }}>
        <ValueSelectInput<Item>
          scale="small"
          searchable
          calloutOffset="xxs"
          onOpeningChange={(isOpen) => {
            if (!isOpen) {
              clearSearchValue();
            }
          }}
          searchInputProps={{
            placeholder: 'Search Content',
          }}
          placeholder="Content to display"
          onFilter={updateSearchValue}
          onClearFilter={clearSearchValue}
          optionsPrefix={
            <OptionContainer>
              <HelpIcon color="BerryBlue" size="s" />
              Only published content will go live.
            </OptionContainer>
          }
          items={filteredOptions}
          selectedItem={selectedItem}
          onSelect={onSelectValue}
        />
      </ValueBox>
      {publishedContentValue && defaultValue?.slug && selectedItem && (
        <TextButton
          onClick={() => window.open(workspacePath(contentPath(selectedItem.id)), '_blank')}
        >
          Open Content
        </TextButton>
      )}
      {!publishedContentValue && defaultValue?.slug && (
        <ToolboxErrorMessage message="The selected Content is not published" />
      )}
    </>
  );
};

export const ChangeContentField = connectWithHammock({
  endpoint: ENDPOINTS.contentMetadata,
  // Make sure hammock doesn't get the id from props.
  id: undefined,
  resourceName: 'contentList',
})(ChangeContentFieldImpl) as InteractionField;
